/* eslint-disable react-refresh/only-export-components -- контекст, хук и провайдер живут вместе, как принято для AuthContext */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, ApiError, DEFAULT_FLAGS, type FeatureFlags, type Me, type PublicSettings } from './api';
import { changeLanguage } from '@/i18n';

/** Сессия пользователя и фичефлаги рабочего пространства. Данные грузятся в эффекте; состояние — loading / error / refetch. */
interface AuthState {
  me: Me | null;
  loading: boolean;
  error: string | null;
  flags: FeatureFlags;
  version: string;
  googleConfigured: boolean;
  refetch: () => Promise<void>;
  setMe: (me: Me | null) => void;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [me, setMeState] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pub, setPub] = useState<PublicSettings | null>(null);

  const loadPublic = useCallback(async () => {
    try {
      setPub(await api.get<PublicSettings>('/settings/public'));
    } catch {
      /* без флагов разделы фазы 2 остаются скрытыми */
    }
  }, []);

  const refetch = useCallback(async () => {
    setError(null);
    try {
      const m = await api.get<Me>('/auth/me');
      setMeState(m);
      // язык из профиля — только если пользователь ещё не выбирал его на этом устройстве; выбор сохраняется в профиль (LanguageSwitcher)
      if (m.locale && !localStorage.getItem('treejar-lang')) changeLanguage(m.locale);
      void loadPublic();
    } catch (e) {
      const err = e as ApiError;
      setMeState(null);
      if (err.status !== 401) setError(err.code || 'network');
    } finally {
      setLoading(false);
    }
  }, [loadPublic]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  const setMe = useCallback(
    (m: Me | null) => {
      setMeState(m);
      if (m) void loadPublic();
    },
    [loadPublic],
  );

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } finally {
      setMeState(null);
      setPub(null);
    }
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      me,
      loading,
      error,
      flags: pub?.featureFlags ?? DEFAULT_FLAGS,
      version: pub?.version ?? '',
      googleConfigured: pub?.googleConfigured ?? false,
      refetch,
      setMe,
      logout,
    }),
    [me, loading, error, pub, refetch, setMe, logout],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth outside AuthProvider');
  return ctx;
}

/** Инициалы для аватара: «Layla Hassan» → LH */
export const initials = (name: string) =>
  name
    .split(/[\s@._-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('') || '?';
